import { useEffect, useMemo, useState, useRef } from 'react';
import moment from 'moment-timezone';
import { COLORS } from '@/ui-library/design-system';
import { getManualAvailableSlots } from '../InterviewManualScheduler/getManualAvailableSlots';
import { schProcessCalendarEvents } from './schProcessCalendarEvents';
import { SchProcessAvailability } from './SchProcessAvailability.helper';
import { useDateFormat } from '@/hooks';
import { bulkManualSchedulerUtils } from '@/pages/KInterviewScheduler/BulkManualScheduler/bulkManualScheduler.utils';

const { isConflictingWithBulkBookedEvents } = bulkManualSchedulerUtils;

export const useCalendarEvents = ({
  interviewerDataForCurrentWeek,
  meetingRoomDataForCurrentWeek,
  interviewDataRef,
  calendarRef,
  isLoading,
  currentWeekRange,
  registerForUpdates,
  isEdit,
  interviewPlan,
  updateSharedData,
  timezone,
  isBulkManual = false,
}) => {
  const dataForCurrentWeek = useMemo(() => {
    return {
      ...interviewerDataForCurrentWeek,
      ...meetingRoomDataForCurrentWeek,
    };
  }, [interviewerDataForCurrentWeek, meetingRoomDataForCurrentWeek]);

  const { bookedSlot, colorCodes } = interviewDataRef.current;

  const [localInterviewers, setLocalInterviewers] = useState<any[]>(
    interviewDataRef.current.interviewers
  );

  const [userMeetingRooms, setUserMeetingRooms] = useState<any[]>(
    interviewDataRef.current.meetingRooms
  );

  const [localInterviewKind, setLocalInterviewKind] = useState(
    interviewDataRef.current.interviewKind
  );

  const [userCalendarInfo, setUserCalendarInfo] = useState(
    interviewDataRef.current.userCalendarInfo
  );

  const [meetingRoomCalendarInfo, setMeetingRoomCalendarInfo] = useState(
    interviewDataRef.current.roomCalendarInfo
  );

  const { dateFormat } = useDateFormat();

  // Registering independent updates for each field
  useEffect(() => {
    const unregisterInterviewers = registerForUpdates(
      'interviewers',
      (data) => {
        const { interviewers = [] } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalInterviewers(interviewers);
      }
    );

    const unregisterInterviewKind = registerForUpdates(
      'interviewKind',
      (data) => {
        const { interviewKind = 'one_on_one' } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setLocalInterviewKind(interviewKind);
      }
    );

    const unregisterUserCalendarInfo = registerForUpdates(
      'userCalendarInfo',
      (data) => {
        const { userCalendarInfo } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setUserCalendarInfo(userCalendarInfo);
      }
    );

    const unregisterUserMeetingRooms = registerForUpdates(
      'meetingRooms',
      (data) => {
        const { meetingRooms } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setUserMeetingRooms(meetingRooms);
      }
    );

    const unregisterMeetingRoomCalendarInfo = registerForUpdates(
      'roomCalendarInfo',
      (data) => {
        const { roomCalendarInfo } =
          data?.fullData ?? interviewDataRef.current ?? {};
        setMeetingRoomCalendarInfo(roomCalendarInfo);
      }
    );

    const unregisterDuration = registerForUpdates(
      'bookedSlot.duration',
      (_data) => {
        // Listener triggers re-render so candidateAvailabilityEvents recalculates
      }
    );

    return () => {
      unregisterInterviewKind();
      unregisterInterviewers();
      unregisterUserCalendarInfo();
      unregisterUserMeetingRooms();
      unregisterMeetingRoomCalendarInfo();
      unregisterDuration();
    };
  }, [registerForUpdates, interviewDataRef]);

  const { allEvents, hasAllDayEvent, nonAvailabilityEvents } = useMemo(() => {
    return schProcessCalendarEvents({
      dataForCurrentWeek,
      colorCodes,
      // @ts-expect-error COLORS type mismatch with schProcessCalendarEvents
      COLORS,
      timezone,
      dateFormat,
    });
  }, [dataForCurrentWeek, colorCodes, timezone, dateFormat]);

  const combinedIds = useMemo(() => {
    return [...localInterviewers, ...userMeetingRooms];
  }, [localInterviewers, userMeetingRooms]);

  const commonTimeAvailability = useMemo(() => {
    // Filter dataForAllWeeks to include only the current interviewers' data
    const filteredDataForAllWeeks: { [weekRange: string]: any } = {};

    filteredDataForAllWeeks[currentWeekRange] = {};
    localInterviewers?.forEach((id) => {
      if (dataForCurrentWeek[id]) {
        filteredDataForAllWeeks[currentWeekRange][id] = dataForCurrentWeek[id];
      }
    });

    if (Object.keys(filteredDataForAllWeeks[currentWeekRange]).length === 0) {
      delete filteredDataForAllWeeks[currentWeekRange];
    }

    if (dataForCurrentWeek) {
      return SchProcessAvailability({
        input: filteredDataForAllWeeks,
        timezone,
        isOneOnOne: localInterviewKind === 'one_on_one',
        // userMeetingRooms,
      });
    }
    return {};
  }, [
    currentWeekRange,
    localInterviewers,
    dataForCurrentWeek,
    timezone,
    localInterviewKind,
  ]);

  // Check if a meeting is booked
  const isMeetingBooked = Boolean(bookedSlot?.start && bookedSlot?.end);

  // Memoize interviewers' availability calculation
  const interviewersAvailability = useMemo(() => {
    return localInterviewers.map((id) => {
      const userInfo = userCalendarInfo[id];
      const hasCalendarConnected = userInfo?.isCalendarConnected === true;
      const hasEvents = userInfo?.zeroEventsByRange
        ? !Object.values(userInfo.zeroEventsByRange).every(
            (zero) => zero === true
          )
        : false;
      return { id, calendar_available: hasCalendarConnected, hasEvents };
    });
  }, [localInterviewers, userCalendarInfo]);

  const roomsAvailability = useMemo(() => {
    return userMeetingRooms.map((id) => {
      const roomInfo = meetingRoomCalendarInfo[id];
      const hasCalendarConnected = roomInfo?.isCalendarConnected === true;
      return { id, calendar_available: hasCalendarConnected, hasEvents: true };
    });
  }, [userMeetingRooms, meetingRoomCalendarInfo]);

  // Memoize the shouldGenerateSlots calculation
  const shouldGenerateSlots = useMemo(() => {
    // No need to check calendars if conditions are met
    if (!combinedIds?.length || isMeetingBooked || isLoading) {
      return false;
    }
    const validInterviewers = localInterviewers.filter(
      (id) => userCalendarInfo[id]?.isCalendarConnected
    );

    return localInterviewKind === 'one_on_one'
      ? [...interviewersAvailability, ...roomsAvailability].some(
          (interviewer) => interviewer.calendar_available
        )
      : validInterviewers.length > 0 &&
          [...interviewersAvailability, ...roomsAvailability].every(
            (interviewer) => interviewer.calendar_available
          );
  }, [
    combinedIds?.length,
    isMeetingBooked,
    isLoading,
    localInterviewers,
    localInterviewKind,
    interviewersAvailability,
    roomsAvailability,
    userCalendarInfo,
  ]);

  // Add a debounced version of isLoading to prevent flickering
  const loadingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const prevInterviewersLengthRef = useRef<number>(0);

  // Debounce the loading state changes
  useEffect(() => {
    // When interviewers are added, immediately set loading state to true
    // This prevents the initial flicker when interviewers are first added
    if (localInterviewers?.length > prevInterviewersLengthRef.current) {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
        loadingTimerRef.current = null;
      }
    }

    // Update the reference for next comparison
    prevInterviewersLengthRef.current = localInterviewers?.length || 0;

    // Cleanup on unmount
    return () => {
      if (loadingTimerRef.current) {
        clearTimeout(loadingTimerRef.current);
      }
    };
  }, [
    isLoading,
    localInterviewers,
    dataForCurrentWeek,
    commonTimeAvailability,
    shouldGenerateSlots,
  ]);

  // Generate available slots, respecting the form's duration
  const availableSlots = useMemo(() => {
    // Use the stable loading state to prevent flickering

    // Determine if we have interviewers selected and need to wait for data
    const hasInterviewers = localInterviewers?.length > 0;

    // Only consider data ready when:
    // 1. No interviewers selected (nothing to load) OR
    // 2. Not in loading state AND we have interviewers AND shouldGenerateSlots is true
    // This ensures we only consider data ready when we have complete data
    const isDataReady =
      hasInterviewers &&
      shouldGenerateSlots &&
      Object.keys(commonTimeAvailability || {}).length > 0;

    // Default week is available when:
    // 1. No interviewers selected OR
    // 2. Meeting is already booked OR
    // 3. Data is not ready yet (still loading)
    const isDefaultWeekAvailable =
      !hasInterviewers || isMeetingBooked || !isDataReady;

    // Check if all selected users have their calendars connected
    // For one-on-one interviews, we need at least one interviewer with a calendar
    // For panel interviews, we need all interviewers to have calendars
    const isAllSelectedUserCalendarConnected =
      localInterviewers.length > 0 &&
      (localInterviewKind === 'one_on_one'
        ? localInterviewers.some((id) => {
            const userInfo = userCalendarInfo[id];
            // At least one interviewer must have a calendar connected and available
            return (
              userInfo?.isCalendarConnected !== false &&
              userInfo?.calendar_available !== false
            );
          })
        : localInterviewers.every((id) => {
            const userInfo = userCalendarInfo[id];
            // All interviewers must have calendars connected and available
            return (
              userInfo?.isCalendarConnected !== false &&
              userInfo?.calendar_available !== false
            );
          }));

    // Only show no slots available when we have complete data that confirms no slots
    // AND we have interviewers selected
    if (
      isDataReady &&
      !isMeetingBooked &&
      shouldGenerateSlots &&
      hasInterviewers
    ) {
      const slots = getManualAvailableSlots(
        commonTimeAvailability,
        allEvents,
        timezone,
        localInterviewers,
        bookedSlot?.duration,
        dateFormat
      );

      // Candidate-availability flow only: when present, hide interviewer (blue) slots.
      // When absent (undefined/[]), filteredSlots === slots — existing flow unchanged.
      const candidateSlots =
        interviewDataRef.current?.candidateAvailabilitySlots ?? [];
      const hasCandidateAvailability = candidateSlots.length > 0;
      const filteredSlots = hasCandidateAvailability ? [] : slots;

      // Only update noSlotsAvailable when data is ready and confirmed
      // AND we have interviewers selected
      const noSlotsAvailable =
        isDataReady && hasInterviewers && slots?.length === 0;
      updateSharedData({
        [`weekAvailability.${currentWeekRange}`]: isDefaultWeekAvailable
          ? true
          : shouldGenerateSlots,
        noSlotsAvailable,
      });

      return filteredSlots;
    }

    // Important: Don't show noSlotsAvailable banner during loading
    // Only show it when we're certain there are no slots AND we have interviewers selected
    // OR when we know that the selected interviewers don't have calendars connected
    const noSlotsAvailable =
      (isDataReady && !isDefaultWeekAvailable && hasInterviewers) ||
      (hasInterviewers &&
        !isAllSelectedUserCalendarConnected &&
        Object.keys(userCalendarInfo).length > 0);

    updateSharedData({
      [`weekAvailability.${currentWeekRange}`]: isDefaultWeekAvailable
        ? true
        : shouldGenerateSlots,
      // Only show noSlotsAvailable when we're sure - never during loading
      noSlotsAvailable,
    });

    return []; // Return an empty array when conditions are not met
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isLoading,
    isMeetingBooked,
    shouldGenerateSlots,
    updateSharedData,
    currentWeekRange,
    commonTimeAvailability,
    allEvents,
    calendarRef,
    localInterviewers,
    bookedSlot?.duration,
    dateFormat,
    timezone,
    interviewDataRef,
  ]);

  const end = useMemo(() => {
    if (!bookedSlot?.start || !bookedSlot?.duration || !timezone) {
      return null; // Handle missing data
    }
    return moment
      .tz(bookedSlot.start, timezone)
      .add(bookedSlot.duration, 'minutes')
      .format('YYYY-MM-DDTHH:mm:ssZ');
  }, [bookedSlot?.start, bookedSlot?.duration, timezone]);

  const bulkBookedEvents = useMemo(() => {
    if (!isBulkManual) {
      return [];
    }
    const { bulkBookedEvents = [] } = interviewDataRef?.current ?? {};
    return Array.isArray(bulkBookedEvents) ? bulkBookedEvents : [];
  }, [interviewDataRef, isBulkManual]);

  const bookedEvent = useMemo(() => {
    if (!isMeetingBooked || !bookedSlot) {
      return [];
    }

    const { interviewerStatus, isOriginalSlotModified } =
      bookedSlot?.extendedProps ?? {};

    const showErrorCondition =
      isBulkManual &&
      Array.isArray(localInterviewers) &&
      interviewerStatus &&
      !isOriginalSlotModified;

    // this is only for bulk manual
    const showError = showErrorCondition
      ? localInterviewers.some((interviewer) => {
          const { has_exceeded_max_workload = false, is_unavailable = false } =
            interviewerStatus?.[interviewer] ?? {};
          return has_exceeded_max_workload || is_unavailable;
        })
      : false;

    const isConflicting = isBulkManual
      ? isConflictingWithBulkBookedEvents(bulkBookedEvents, {
          start: bookedSlot.start,
          end: bookedSlot.end,
          timezone,
        })
      : false;

    // Suppress the conflict (red) tint entirely in the Schedule All flow when
    // candidate availability slots are present — the red overlay shouldn't
    // override the candidate's green availability window.
    const hasCandidateAvailability = Array?.isArray(
      interviewDataRef.current?.candidateAvailabilitySlots
    )
      ? interviewDataRef.current.candidateAvailabilitySlots?.length > 0
      : false;
    const applyConflictTint =
      (isConflicting || showError) && !hasCandidateAvailability;

    return [
      {
        ...bookedSlot,
        end,
        classNames: `booked-event ${bookedSlot?.classNames ?? ''}`.trim(),
        ...(applyConflictTint && {
          backgroundColor: 'var(--kula-colors-error-100)',
          borderColor: 'var(--kula-colors-error-200)',
        }),
      },
    ];
  }, [
    isMeetingBooked,
    bookedSlot,
    localInterviewers,
    isBulkManual,
    bulkBookedEvents,
    timezone,
    end,
    interviewDataRef,
  ]);

  const filteredAllEvents = useMemo(() => {
    if (!isEdit) {
      return allEvents;
    }

    return allEvents.filter((eve) => {
      const { end_time, start_time, subject } = eve ?? {};

      if (
        end_time === interviewPlan?.end_time &&
        start_time === interviewPlan?.start_time &&
        subject === interviewPlan?.name
      ) {
        return false;
      } else {
        return true;
      }
    });
  }, [allEvents, isEdit, interviewPlan]);

  // Generate background events to grey out times outside the common availability window
  // This shows users which time slots are outside interviewers' working hours
  const unavailableTimeBackgroundEvents = useMemo(() => {
    const bgEvents: any[] = [];
    const datesSet = interviewDataRef.current?.datesSet;

    // Get the current week's availability (intersection for panel, union for one-on-one)
    const currentWeekAvailability =
      commonTimeAvailability[currentWeekRange] || [];

    // Check if we have interviewers selected (to determine if we should grey out when no availability)
    const hasInterviewersSelected =
      localInterviewers && localInterviewers.length > 0;

    if (datesSet?.start) {
      // Get the week's date range
      const weekStart = moment.tz(datesSet.start, timezone).startOf('week');

      // For each day in the week, create background events for unavailable times
      for (let dayOffset = 0; dayOffset < 7; dayOffset++) {
        const currentDay = weekStart.clone().add(dayOffset, 'days');
        const dayStart = currentDay.clone().startOf('day');
        const dayEnd = currentDay.clone().endOf('day');

        // Skip past days (already handled by the past time background event)
        const now = moment().tz(timezone);
        if (currentDay.isBefore(now, 'day')) {
          continue;
        }

        // Find availability slots that fall on this day
        const dayAvailabilitySlots: {
          start: moment.Moment;
          end: moment.Moment;
        }[] = [];

        // Only look for availability slots if we have some availability data
        if (currentWeekAvailability?.length > 0) {
          currentWeekAvailability.forEach(
            (slot: { start: string; end: string }) => {
              const slotStart = moment.utc(slot.start).tz(timezone);
              const slotEnd = moment.utc(slot.end).tz(timezone);

              // Check if this slot overlaps with the current day
              if (slotStart.isBefore(dayEnd) && slotEnd.isAfter(dayStart)) {
                // Clip the slot to this day's boundaries
                const effectiveStart = moment.max(slotStart, dayStart);
                const effectiveEnd = moment.min(slotEnd, dayEnd);

                if (effectiveEnd.isAfter(effectiveStart)) {
                  dayAvailabilitySlots.push({
                    start: effectiveStart,
                    end: effectiveEnd,
                  });
                }
              }
            }
          );
        }

        // Sort availability slots by start time
        dayAvailabilitySlots.sort(
          (a, b) => a.start.valueOf() - b.start.valueOf()
        );

        // Create background events for gaps (unavailable times)
        let currentTime = dayStart.clone();

        dayAvailabilitySlots.forEach((slot) => {
          // If there's a gap before this availability slot, create a background event
          if (slot.start.isAfter(currentTime)) {
            bgEvents.push({
              start: currentTime.format('YYYY-MM-DDTHH:mm:ssZ'),
              end: slot.start.format('YYYY-MM-DDTHH:mm:ssZ'),
              display: 'background',
              classNames: 'bg-unavailable-working-hours',
              color: 'transparent',
            });
          }
          // Move current time to the end of this availability slot
          if (slot.end.isAfter(currentTime)) {
            currentTime = slot.end.clone();
          }
        });

        // If there's remaining time after the last availability slot, create a background event
        // This also handles the case when NO availability exists (entire day is greyed out)
        // Only grey out if we have interviewers selected (otherwise it's just an empty state)
        if (currentTime.isBefore(dayEnd) && hasInterviewersSelected) {
          bgEvents.push({
            start: currentTime.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: dayEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
            display: 'background',
            classNames: 'bg-unavailable-working-hours',
            color: 'transparent',
          });
        }
      }
    }

    return bgEvents;
  }, [
    commonTimeAvailability,
    currentWeekRange,
    interviewDataRef,
    localInterviewers,
    timezone,
  ]);

  // Only used when candidate availability is present; returns [] otherwise (no impact on events).
  const candidateAvailabilityEvents = useMemo(() => {
    const rawSlots = interviewDataRef.current?.candidateAvailabilitySlots;
    if (!rawSlots?.length)
      return { events: [], overlappingBusyKeys: new Set<string>() };
    const candidateName =
      interviewDataRef.current?.candidate?.full_name ||
      interviewDataRef.current?.candidate?.name ||
      '';
    const commonAvailability = commonTimeAvailability?.[currentWeekRange] || [];
    // When we have no interviewer availability data, show mutual opacity for all candidate slots
    const hasInterviewerAvailabilityData = commonAvailability.length > 0;
    const busyEvents = (allEvents || []).filter(
      (e: any) => e?.extendedProps?.type === 'busy'
    );

    // Merge overlapping/adjacent candidate availability slots to prevent duplicate calendar segments
    const sortedSlots = [...rawSlots]
      .map((s: { start_at: string; end_at: string }) => ({
        start_at: s.start_at,
        end_at: s.end_at,
      }))
      .sort(
        (a, b) => moment(a.start_at).valueOf() - moment(b.start_at).valueOf()
      );
    const slots: { start_at: string; end_at: string }[] = [];
    sortedSlots.forEach((s) => {
      const last = slots[slots.length - 1];
      if (last && moment(last.end_at).isSameOrAfter(moment(s.start_at))) {
        if (moment(s.end_at).isAfter(moment(last.end_at))) {
          last.end_at = s.end_at;
        }
      } else {
        slots.push({ start_at: s.start_at, end_at: s.end_at });
      }
    });
    const events: any[] = [];
    const overlappingBusyKeys = new Set<string>();
    let eventId = 0;
    // Check if a segment's overlap with common availability is >= interview duration
    const isSegmentMutual = (
      segStart: moment.Moment,
      segEnd: moment.Moment
    ) => {
      if (!hasInterviewerAvailabilityData) return true;
      return commonAvailability.some(
        (avail: { start: string; end: string }) => {
          const overlapStart = moment.max(segStart, moment(avail.start));
          const overlapEnd = moment.min(segEnd, moment(avail.end));
          return (
            overlapEnd.isAfter(overlapStart) &&
            overlapEnd.diff(overlapStart, 'minutes') >= bookedSlot?.duration
          );
        }
      );
    };

    slots.forEach(
      (slot: { start_at: string; end_at: string }, slotIdx: number) => {
        const slotStart = moment(slot.start_at).tz(timezone);
        const slotEnd = moment(slot.end_at).tz(timezone);
        const overlappingBusies = busyEvents
          .filter(
            (busy: { start: string; end: string }) =>
              slotStart.isBefore(moment(busy.end)) &&
              slotEnd.isAfter(moment(busy.start))
          )
          .map((busy: { start: string; end: string }) => {
            overlappingBusyKeys.add(`${busy.start}_${busy.end}`);
            return {
              start: moment(busy.start),
              end: moment(busy.end),
            };
          })
          .sort((a, b) => a.start.valueOf() - b.start.valueOf());
        let currentStart = slotStart.clone();
        overlappingBusies.forEach((busy) => {
          const busyStart = moment.max(busy.start.clone(), slotStart.clone());
          const busyEnd = moment.min(busy.end.clone(), slotEnd.clone());
          if (busyEnd.isSameOrBefore(busyStart)) return;
          // Skip busy events already covered by a previous busy
          if (busyEnd.isSameOrBefore(currentStart)) return;
          if (currentStart.isBefore(busyStart)) {
            const gapEnd = busyStart.clone();
            const segIsMutual = isSegmentMutual(currentStart, gapEnd);
            const noBusyOverlapClass = !segIsMutual
              ? ' candidate-availability-event--no-busy-overlap'
              : '';
            events.push({
              id: `candidate-avail-${slotIdx}-${eventId++}`,
              start: currentStart.format('YYYY-MM-DDTHH:mm:ssZ'),
              end: gapEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
              backgroundColor: 'var(--kula-colors-pastel-2-light)',
              textColor: 'var(--kula-colors-pastel-2-dark)',
              editable: false,
              durationEditable: false,
              startEditable: false,
              classNames: `candidate-availability-event candidate-availability-event--${segIsMutual ? 'mutual' : 'no-mutual'}${noBusyOverlapClass}`,
              extendedProps: {
                type: 'candidate-availability',
                category: 'candidate-availability',
                description: `Available ${currentStart.format('hh:mm A')} - ${gapEnd.format('hh:mm A')}`,
                candidateName,
                isMutual: segIsMutual,
              },
            });
          }
          // Clamp during-busy segment start to currentStart to prevent overlapping with previous segments
          const segmentStart = moment.max(currentStart.clone(), busyStart);
          events.push({
            id: `candidate-avail-${slotIdx}-${eventId++}`,
            start: segmentStart.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: busyEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
            backgroundColor: 'var(--kula-colors-pastel-2-light)',
            textColor: 'var(--kula-colors-pastel-2-dark)',
            editable: false,
            durationEditable: false,
            startEditable: false,
            classNames: `candidate-availability-event candidate-availability-event--no-mutual`,
            extendedProps: {
              type: 'candidate-availability',
              category: 'candidate-availability',
              description: `Available ${segmentStart.format('hh:mm A')} - ${busyEnd.format('hh:mm A')}`,
              candidateName,
              isMutual: false,
            },
          });
          currentStart = busyEnd.clone();
        });
        if (currentStart.isBefore(slotEnd)) {
          const segIsMutual = isSegmentMutual(currentStart, slotEnd);
          const noBusyOverlapClass = !segIsMutual
            ? ' candidate-availability-event--no-busy-overlap'
            : '';
          events.push({
            id: `candidate-avail-${slotIdx}-${eventId++}`,
            start: currentStart.format('YYYY-MM-DDTHH:mm:ssZ'),
            end: slotEnd.format('YYYY-MM-DDTHH:mm:ssZ'),
            backgroundColor: 'var(--kula-colors-pastel-2-light)',
            textColor: 'var(--kula-colors-pastel-2-dark)',
            editable: false,
            durationEditable: false,
            startEditable: false,
            classNames: `candidate-availability-event candidate-availability-event--${segIsMutual ? 'mutual' : 'no-mutual'}${noBusyOverlapClass}`,
            extendedProps: {
              type: 'candidate-availability',
              category: 'candidate-availability',
              description: `Available ${currentStart.format('hh:mm A')} - ${slotEnd.format('hh:mm A')}`,
              candidateName,
              isMutual: segIsMutual,
            },
          });
        }
      }
    );

    // Merge adjacent non-mutual during-busy segments into single events
    const mergedEvents: any[] = [];
    const isBusyOverlapSegment = (event: any) =>
      !event.extendedProps.isMutual &&
      !event.classNames.includes(
        'candidate-availability-event--no-busy-overlap'
      );
    for (let i = 0; i < events.length; i++) {
      const current = events[i];
      if (isBusyOverlapSegment(current) && mergedEvents.length > 0) {
        const prev = mergedEvents[mergedEvents.length - 1];
        if (isBusyOverlapSegment(prev) && prev.end === current.start) {
          const mergedStart = moment(prev.start);
          const mergedEnd = moment(current.end);
          prev.end = current.end;
          prev.extendedProps.description = `Available ${mergedStart.format('hh:mm A')} - ${mergedEnd.format('hh:mm A')}`;
          continue;
        }
      }
      mergedEvents.push(current);
    }
    return { events: mergedEvents, overlappingBusyKeys };
  }, [
    interviewDataRef,
    currentWeekRange,
    commonTimeAvailability,
    allEvents,
    timezone,
    bookedSlot?.duration,
  ]);

  const events = useMemo(() => {
    const busyEventsWithOverlapClass = filteredAllEvents.map((event: any) => {
      if (
        event?.extendedProps?.type === 'busy' &&
        candidateAvailabilityEvents.overlappingBusyKeys.has(
          `${event.start}_${event.end}`
        )
      ) {
        return {
          ...event,
          classNames:
            `${event.classNames || ''} busy-event--has-candidate-overlap`.trim(),
        };
      }
      return event;
    });
    return [
      ...busyEventsWithOverlapClass,
      ...bookedEvent,
      ...(bulkBookedEvents?.filter((event) => !event?.isActive) ?? []),
      ...nonAvailabilityEvents,
      ...availableSlots,
      ...unavailableTimeBackgroundEvents,
      ...candidateAvailabilityEvents.events,
      {
        start: moment()
          ?.tz(timezone)
          ?.subtract(10, 'year')
          ?.format('YYYY-MM-DDTHH:mm:ssZ'),
        end: moment()?.tz(timezone)?.format('YYYY-MM-DDTHH:mm:ssZ'),
        display: 'background',
        classNames: 'bg-disabled-slots',
        color: 'transparent',
      },
    ];
  }, [
    filteredAllEvents,
    bookedEvent,
    bulkBookedEvents,
    nonAvailabilityEvents,
    availableSlots,
    unavailableTimeBackgroundEvents,
    candidateAvailabilityEvents,
    timezone,
  ]);

  return {
    events,
    hasAllDayEvent,
    shouldGenerateSlots,
    hasCandidateAvailability: candidateAvailabilityEvents.events.length > 0,
  };
};
